import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { vendorAPI } from '../../services/api';
import { Notification } from '../../types';

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationsState = {
  notifications: [],
  unreadCount: 0,
  pagination: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (
    params: { page?: number; limit?: number; unread?: boolean; type?: string; sort?: string; search?: string } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await vendorAPI.getNotifications(params);
      const body = response.data as any;
      return {
        notifications: body.data?.notifications ?? [],
        unread: body.data?.unread ?? 0,
        pagination: body.pagination ?? null,
      };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to fetch notifications';
      return rejectWithValue(errorMessage);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markNotificationAsRead',
  async (id: string, { rejectWithValue }) => {
    try {
      await vendorAPI.markNotificationAsRead(id);
      return { id };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to mark notification as read';
      return rejectWithValue(errorMessage);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllNotificationsAsRead',
  async (_, { rejectWithValue }) => {
    try {
      const response = await vendorAPI.markAllNotificationsAsRead();
      return (response.data as any).data?.updated ?? 0;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to mark all notifications as read';
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkMarkNotificationsAsRead = createAsyncThunk(
  'notifications/bulkMarkNotificationsAsRead',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await vendorAPI.bulkMarkNotificationsAsRead(ids);
      return ids;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to mark selected notifications as read';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteNotificationById = createAsyncThunk(
  'notifications/deleteNotificationById',
  async (id: string, { rejectWithValue }) => {
    try {
      await vendorAPI.deleteNotification(id);
      return { id };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete notification';
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkDeleteNotifications = createAsyncThunk(
  'notifications/bulkDeleteNotifications',
  async (ids: string[], { rejectWithValue }) => {
    try {
      await vendorAPI.bulkDeleteNotifications(ids);
      return ids;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete selected notifications';
      return rejectWithValue(errorMessage);
    }
  }
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addNotification: (state, action) => {
      state.notifications.unshift(action.payload);
      if (!action.payload.read) {
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action) => {
      const index = state.notifications.findIndex(n => n.id === action.payload);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (!notification.read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications;
        state.unreadCount = action.payload.unread;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const n = state.notifications.find((x) => x.id === action.payload.id);
        if (n && !n.read) {
          n.read = true;
          n.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach((n) => {
          n.read = true;
          if (!n.readAt) n.readAt = new Date().toISOString();
        });
        state.unreadCount = 0;
      })
      .addCase(bulkMarkNotificationsAsRead.fulfilled, (state, action) => {
        const ids = new Set(action.payload);
        state.notifications.forEach((n) => {
          if (ids.has(n.id) && !n.read) {
            n.read = true;
            n.readAt = new Date().toISOString();
          }
        });
        state.unreadCount = state.notifications.filter((n) => !n.read).length;
      })
      .addCase(deleteNotificationById.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter((n) => n.id !== action.payload.id);
        state.unreadCount = state.notifications.filter((n) => !n.read).length;
      })
      .addCase(bulkDeleteNotifications.fulfilled, (state, action) => {
        const ids = new Set(action.payload);
        state.notifications = state.notifications.filter((n) => !ids.has(n.id));
        state.unreadCount = state.notifications.filter((n) => !n.read).length;
      });
  },
});

export const { clearError, addNotification, removeNotification } = notificationsSlice.actions;
export default notificationsSlice.reducer;
